// =============================================================================
// MeuDriver — Analytics do Admin (H2)
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import type {
  RevenueSummary,
  DailyBookingRow,
  TopInstructorRow,
} from '@/types/database'

// ─── Chaves de query ──────────────────────────────────────────────────────────

const KEYS = {
  revenueSummary: (days: number) => ['admin_analytics', 'revenue_summary', days] as const,
  dailyBookings: (days: number) => ['admin_analytics', 'daily_bookings', days] as const,
  topInstructors: (limit: number) => ['admin_analytics', 'top_instructors', limit] as const,
}

const STALE = 5 * 60_000 // 5 min — dados admin não precisam ser em tempo real

// ─── useRevenueMetrics ────────────────────────────────────────────────────────

/**
 * KPIs de receita e usuários via RPC get_revenue_summary.
 * Apenas admin pode chamar — o banco valida via SECURITY DEFINER.
 */
export function useRevenueMetrics(days = 30) {
  return useQuery({
    queryKey: KEYS.revenueSummary(days),
    staleTime: STALE,
    queryFn: async (): Promise<RevenueSummary> => {
      const { data, error } = await supabase.rpc('get_revenue_summary', { p_days: days })
      if (error) throw new Error(error.message)
      // Supabase retorna JSONB como objeto direto
      return data as RevenueSummary
    },
  })
}

// ─── useBookingMetrics (alias) ────────────────────────────────────────────────

/**
 * Mesmo dados de useRevenueMetrics, exposto com nome semântico de bookings.
 */
export function useBookingMetrics(days = 30) {
  return useRevenueMetrics(days)
}

// ─── useDailyBookings ────────────────────────────────────────────────────────

/**
 * Série temporal: bookings e receita por dia via RPC get_daily_bookings.
 */
export function useDailyBookings(days = 30) {
  return useQuery({
    queryKey: KEYS.dailyBookings(days),
    staleTime: STALE,
    queryFn: async (): Promise<DailyBookingRow[]> => {
      const { data, error } = await supabase.rpc('get_daily_bookings', { p_days: days })
      if (error) throw new Error(error.message)
      return (data ?? []) as DailyBookingRow[]
    },
  })
}

// ─── useTopInstructors ────────────────────────────────────────────────────────

/**
 * Top instrutores por receita via RPC get_top_instructors.
 */
export function useTopInstructors(limit = 10) {
  return useQuery({
    queryKey: KEYS.topInstructors(limit),
    staleTime: STALE,
    queryFn: async (): Promise<TopInstructorRow[]> => {
      const { data, error } = await supabase.rpc('get_top_instructors', { p_limit: limit })
      if (error) throw new Error(error.message)
      return (data ?? []) as TopInstructorRow[]
    },
  })
}

// ─── useUserGrowth (alias para daily com foco em usuários) ───────────────────

/**
 * Crescimento de novos usuários — usa o summary agrupado por período.
 */
export function useUserGrowth(days = 30) {
  return useRevenueMetrics(days)
}

// ─── Q3: useRevenueAnalytics ──────────────────────────────────────────────────

/** Linha retornada por get_revenue_analytics */
export interface RevenueAnalyticsRow {
  month_label: string     // 'YYYY-MM'
  gmv: number
  commission: number
  new_students: number
  new_instructors: number
  bookings_count: number
}

/**
 * GMV e comissão por mês via RPC get_revenue_analytics.
 * Apenas admin — validado no banco.
 */
export function useRevenueAnalytics(months = 12) {
  return useQuery({
    queryKey: ['admin_analytics', 'revenue_analytics', months] as const,
    staleTime: STALE,
    queryFn: async (): Promise<RevenueAnalyticsRow[]> => {
      const { data, error } = await supabase.rpc('get_revenue_analytics', { p_months: months })
      if (error) throw new Error(error.message)
      return (data ?? []) as RevenueAnalyticsRow[]
    },
  })
}

// ─── Q3: useTopInstructorsAnalytics ──────────────────────────────────────────

/** Linha retornada por get_top_instructors_analytics */
export interface TopInstructorAnalyticsRow {
  instructor_id: string
  instructor_name: string | null
  avatar_url: string | null
  gmv: number
  booking_count: number
  avg_rating: number | null
}

/**
 * Top instrutores por GMV via RPC get_top_instructors_analytics.
 * Parâmetro month no formato 'YYYY-MM' (opcional).
 */
export function useTopInstructorsAnalytics(limit = 10, month?: string) {
  return useQuery({
    queryKey: ['admin_analytics', 'top_instructors_analytics', limit, month ?? null] as const,
    staleTime: STALE,
    queryFn: async (): Promise<TopInstructorAnalyticsRow[]> => {
      const { data, error } = await supabase.rpc('get_top_instructors_analytics', {
        p_limit: limit,
        p_month: month ?? null,
      })
      if (error) throw new Error(error.message)
      return (data ?? []) as TopInstructorAnalyticsRow[]
    },
  })
}

// ─── Q3: useConversionAnalytics ───────────────────────────────────────────────

export interface ConversionAnalytics {
  total_registrations: number
  total_first_bookings: number
  conversion_rate: number
  avg_days_to_first_booking: number
}

/**
 * Métricas de conversão cadastro→primeira aula via RPC get_conversion_analytics.
 */
export function useConversionAnalytics() {
  return useQuery({
    queryKey: ['admin_analytics', 'conversion'] as const,
    staleTime: STALE,
    queryFn: async (): Promise<ConversionAnalytics> => {
      const { data, error } = await supabase.rpc('get_conversion_analytics')
      if (error) throw new Error(error.message)
      return data as ConversionAnalytics
    },
  })
}

// ─── useSubscriptionMetrics ──────────────────────────────────────────────────

/**
 * MRR estimado, assinaturas ativas, churn.
 * Usa a RPC get_subscription_metrics() (SECURITY DEFINER) que verifica
 * internamente se o chamador é admin — elimina as queries diretas às tabelas
 * de assinatura que expunham dados financeiros para qualquer authenticated.
 */
export function useSubscriptionMetrics() {
  return useQuery({
    queryKey: ['admin_analytics', 'subscriptions'],
    staleTime: STALE,
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_subscription_metrics')
      if (error) throw new Error(error.message)
      // A RPC retorna JSONB; Supabase entrega como objeto direto
      return data as {
        mrr: number
        activeStudentSubs: number
        activeInstructorSubs: number
        totalActive: number
        cancelledLast30: number
        churnRate: number
      }
    },
  })
}
